/**
 * Capitulos del recorrido de demostracion.
 *
 * El recorrido es un argumento de venta, no un manual. De ahi tres reglas de
 * escritura que mandan sobre cualquier ganas de explicar:
 *
 *   1. UN CAPITULO, UNA RAZON PARA QUERERLO. Titulo corto que promete algo,
 *      dos beneficios como mucho, y la escena que lo demuestra. Quien ve esto
 *      esta parado frente a un mostrador, no leyendo documentacion.
 *   2. SE HABLA DE LO QUE GANA, NO DE LO QUE PASA SI FALLA. Nadie compra
 *      pensando en el dia que se caiga la luz. La velocidad se vende por lo que
 *      es —cobrar sin esperas— y no por el desastre del que salva.
 *   2b. NO SE PROMETE NADA QUE LE COBRE AL ESTUDIO DESPUES. Ni "sin
 *      mensualidad" —hay cobro por mantenimiento, mejoras, dominio y base— ni
 *      "sin nube", porque el sistema tambien vive en linea. Una promesa que el
 *      cliente puede desmentir el primer dia cuesta mas que la que nunca se
 *      hizo, y una que contradice el precio sabotea la venta desde adentro.
 *   3. LA ESCENA CARGA EL PESO. Si un dato cabe en la pantallita animada, no se
 *      repite en el texto. El numero convence mas escrito en su renglon.
 *
 * Ademas: no se promete nada que el sistema no haga. Cada afirmacion esta
 * respaldada por una pantalla o una regla real del codigo, y los importes salen
 * de `data.ts`, que los calcula con las funciones de produccion de
 * @abarrotes/shared. Nada esta pintado.
 *
 * COMO SE ANIMAN
 * Las escenas se arman por "tiempos" (beats): marcas en milisegundos desde que
 * el capitulo llega a pantalla, que van revelando renglones como si alguien
 * estuviera operando la tienda. Los doce capitulos estan montados a la vez —el
 * recorrido es un carril que se desplaza— asi que los beats corren solo para el
 * capitulo activo: `active` manda, no el montaje.
 *
 * Con `prefers-reduced-motion` cada escena aparece ya terminada.
 *
 * Las escenas son ilustracion, no interfaz: van con `aria-hidden` para que el
 * lector de pantalla lea el argumento y no el teatro. El oro de la marca vive
 * en el marco del recorrido y en los capitulos donde firma Aziru; dentro de las
 * escenas mandan los colores semanticos de siempre: jade, ambar, rojo.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Archive, Check, ShieldCheck, Smartphone, Sparkles, Store, Zap } from 'lucide-react';
import {
  COLLECTION_URGENCY_LABELS,
  CONFIDENCE_LABELS,
  formatMoney,
  formatQty,
  URGENCY_LABELS,
} from '@abarrotes/shared';
import { useBranding } from '../../lib/branding.js';
import { DURATION, EASE, RISE, STAGGER_PARENT } from '../../lib/motion.js';
import { AnimatedAmount } from '../../components/AnimatedAmount.js';
import { Barcode } from '../../components/Barcode.js';
import { BarChart } from '../../components/Chart.js';
import { BrandLogo } from '../../components/BrandLogo.js';
import { Badge, Button, cx } from '../../components/ui.js';
import { WhatsAppIcon } from '../../components/WhatsAppIcon.js';
import { AziruCard, AziruLockup } from './aziru.js';
import {
  AGENDA,
  BALANCE_AFTER_SALE,
  CASH_CUT,
  CHANGE_CENTS,
  CREDIT_CUSTOMER,
  CREDIT_SALE_CENTS,
  DAY_HOURS,
  DAY_PROFIT_CENTS,
  DAY_TICKETS,
  DAY_TOTAL_CENTS,
  demoLineTotal,
  EXPENSES_ROWS,
  GUAJILLO,
  INVENTORY_ROWS,
  PEAK_HOUR_INDEX,
  PEAK_HOUR_LABEL,
  QUICK_BUTTONS,
  RECEIVED_CENTS,
  RESTOCK,
  RESTOCK_LOW,
  SCALE_CODE,
  SCALE_LINE_CENTS,
  SCALE_QTY_MILLI,
  SCALE_SEGMENTS,
  SHRINK,
  STORES_LIVE,
  STORES_TOTAL_CENTS,
  STORES_TOTAL_TICKETS,
  TICKET_LINES,
  ticketTotalCents,
  TOP_PRODUCTS,
  type DemoCollectionUrgency,
  type DemoStockState,
} from './data.js';

// ---------------------------------------------------------------------------
// Contrato con el marco del recorrido
// ---------------------------------------------------------------------------

export interface ChapterProps {
  /** True cuando este capitulo es el que se esta viendo. */
  active: boolean;
  /** False con el recorrido en pausa: las escenas se congelan donde van. */
  playing: boolean;
  /** Solo los usa el capitulo de cierre. */
  onRestart?: () => void;
  onExit?: () => void;
}

export interface ChapterDef {
  id: string;
  /** Se lee en el pie: "3 de 12 · La caja". */
  title: string;
  /** Milisegundos que dura el capitulo cuando el recorrido avanza solo. */
  duration: number;
  /**
   * El puente al siguiente capitulo: la pregunta que este deja abierta y el
   * que sigue contesta. Es lo que convierte doce laminas en un razonamiento
   * seguido, y lo que invita a deslizar hacia abajo.
   */
  next?: string;
  /** True donde firma Aziru: llevan la lluvia de semillas de fondo. */
  identity?: boolean;
  Component: (props: ChapterProps) => ReactNode;
}

// ---------------------------------------------------------------------------
// Tiempos (beats)
// ---------------------------------------------------------------------------

/**
 * Cuantas marcas de `marks` ya se cumplieron desde que el capitulo se activo.
 *
 * La pausa se respeta de verdad: al pausar se guarda cuanto faltaba para la
 * siguiente marca y al reanudar se retoma desde ahi. Con `instant` (movimiento
 * reducido) todas se dan por cumplidas: la escena aparece terminada.
 */
function useBeats(marks: readonly number[], running: boolean, instant: boolean): number {
  const [count, setCount] = useState(instant ? marks.length : 0);
  const countRef = useRef(count);
  const remaining = useRef(marks[0] ?? 0);

  useEffect(() => {
    if (instant || !running || countRef.current >= marks.length) return;

    let timer = 0;
    let startedAt = performance.now();

    const arm = (): void => {
      startedAt = performance.now();
      timer = window.setTimeout(() => {
        const fired = countRef.current;
        countRef.current = fired + 1;
        setCount(countRef.current);

        const current = marks[fired] ?? 0;
        const next = marks[fired + 1];
        if (next !== undefined) {
          remaining.current = next - current;
          arm();
        }
      }, remaining.current);
    };
    arm();

    return () => {
      window.clearTimeout(timer);
      remaining.current = Math.max(0, remaining.current - (performance.now() - startedAt));
    };
  }, [running, instant, marks]);

  return count;
}

// ---------------------------------------------------------------------------
// Piezas compartidas de las escenas
// ---------------------------------------------------------------------------

/** Un bloque que entra cuando su beat se cumple. No hay salidas: solo se suma. */
function Reveal({
  when,
  children,
  className,
  pop = false,
}: {
  when: boolean;
  children: ReactNode;
  className?: string;
  /** Entrada con escala, para el momento fuerte de la escena. */
  pop?: boolean;
}): ReactNode {
  const reduced = useReducedMotion();
  if (!when) return null;

  return (
    <motion.div
      className={className}
      initial={reduced ? false : pop ? { opacity: 0, scale: 0.94 } : { opacity: 0, y: 10 }}
      animate={pop ? { opacity: 1, scale: 1 } : { opacity: 1, y: 0 }}
      transition={{ duration: DURATION.quick, ease: EASE.out }}
    >
      {children}
    </motion.div>
  );
}

/**
 * Argumento del capitulo: etiqueta, promesa y —cuando hace falta— dos
 * beneficios. Entra escalonado de arriba abajo, como la pantalla de acceso.
 */
function Copy({
  kicker,
  title,
  lead,
  points,
}: {
  kicker: string;
  title: string;
  lead?: string;
  /** Dos como maximo. Si hacen falta tres, el capitulo esta haciendo dos cosas. */
  points?: readonly string[];
}): ReactNode {
  const reduced = useReducedMotion();
  const variants = reduced ? undefined : RISE;
  const transition = { duration: DURATION.normal, ease: EASE.out };

  return (
    <motion.div
      className="max-w-lg"
      variants={reduced ? undefined : STAGGER_PARENT}
      initial="hidden"
      animate="visible"
    >
      <motion.p
        variants={variants}
        transition={transition}
        className="text-[11.5px] font-semibold uppercase tracking-[0.2em] text-primary"
      >
        {kicker}
      </motion.p>

      <motion.h2
        variants={variants}
        transition={transition}
        className="mt-2.5 text-[23px] leading-[1.12] md:text-[28px] xl:text-[36px] font-semibold tracking-tight text-balance"
      >
        {title}
      </motion.h2>

      {lead ? (
        <motion.p
          variants={variants}
          transition={transition}
          className="mt-3 text-[13.5px] md:text-[15px] leading-relaxed text-muted text-pretty"
        >
          {lead}
        </motion.p>
      ) : null}

      {/*
       * En el telefono los puntos NO se muestran. Un iPhone con el navegador
       * abierto deja unos 500 px de escenario; el titular, la linea y la escena
       * ya cuentan el argumento completo, y los puntos son lo unico que se
       * puede ceder sin perder la promesa del capitulo. En tablet y escritorio
       * si aparecen, que es donde el espacio los paga.
       */}
      {points && points.length > 0 ? (
        <motion.ul
          variants={variants}
          transition={transition}
          className="hidden sm:block mt-4 space-y-2"
        >
          {points.map((point) => (
            <li
              key={point}
              className="flex items-start gap-2.5 text-[12.5px] md:text-[14px] leading-snug text-muted"
            >
              <Check className="size-3.5 md:size-4 mt-0.5 shrink-0 text-primary" aria-hidden />
              <span className="text-pretty">{point}</span>
            </li>
          ))}
        </motion.ul>
      ) : null}
    </motion.div>
  );
}

/**
 * Argumento a la izquierda, escena a la derecha; apilado solo en el telefono.
 *
 * Las dos columnas entran en `sm` (640 px), no en `lg`. El capitulo tiene que
 * caber de una sola vista: una demostracion que obliga a desplazarse dentro de
 * la lamina deja de verse como una pieza. Apilados, el argumento y la escena
 * suman mas alto del que hay en una ventana ancha pero baja —una portatil de 13
 * pulgadas, una tablet acostada, un navegador con media pantalla—, que es justo
 * donde esto se enseña.
 *
 * Por debajo de 640 px se apila, y ahi no hay problema: un telefono es angosto
 * pero alto, y el alto es lo que hace falta.
 */
function Split({ copy, visual }: { copy: ReactNode; visual: ReactNode }): ReactNode {
  return (
    /*
     * `grid-cols-[minmax(0,1fr)]` en el caso base no es redundante.
     *
     * Las dos columnas de `sm` ya llevaban `minmax(0,...)`, que es lo correcto,
     * pero apilado —o sea en el telefono— la rejilla se quedaba con su pista
     * automatica, y una pista automatica mide al menos el contenido sin partir
     * de su celda. Basta un hijo que no sepa encogerse (el codigo de barras del
     * capitulo del granel traia su ancho en milimetros) para que la pista crezca
     * por encima de la pantalla. A 320 px el capitulo medio 330 y el carril,
     * que recorta el eje X, se comio los 36 px de diferencia: el titular y el
     * texto salian cortados a la derecha.
     *
     * El minimo cero le devuelve el mando al contenedor en las dos formas.
     */
    <div className="w-full max-w-6xl mx-auto grid gap-4 sm:gap-6 lg:gap-10 xl:gap-14 grid-cols-[minmax(0,1fr)] sm:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] sm:items-center">
      {copy}
      {/* La escena es ilustracion: el lector de pantalla se queda con el texto. */}
      <div aria-hidden>{visual}</div>
    </div>
  );
}

/** Marco de una escena: una tarjeta flotante que imita la pantalla real. */
function Panel({
  title,
  badge,
  children,
  className,
}: {
  title?: string;
  badge?: ReactNode;
  children: ReactNode;
  className?: string;
}): ReactNode {
  return (
    <div
      className={cx(
        'rounded-2xl border border-line bg-surface shadow-float overflow-hidden',
        className,
      )}
    >
      {title ? (
        <header className="flex items-center justify-between gap-3 px-4 h-10 border-b border-line bg-surface-2/60">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted">
            {title}
          </span>
          {badge}
        </header>
      ) : null}
      {children}
    </div>
  );
}

/** El punto verde con latido de "esto esta vivo", como el del marco real. */
function LiveDot({ label = 'En vivo' }: { label?: string }): ReactNode {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-primary">
      <span className="relative inline-flex size-2">
        <span className="absolute inset-0 rounded-full bg-primary animate-live-ping" />
        <span className="relative size-2 rounded-full bg-primary" />
      </span>
      {label}
    </span>
  );
}

function MiniStat({
  label,
  value,
  tone = 'ink',
}: {
  label: string;
  value: ReactNode;
  tone?: 'ink' | 'primary' | 'warn';
}): ReactNode {
  const toneCls = tone === 'primary' ? 'text-primary' : tone === 'warn' ? 'text-warn' : 'text-ink';
  return (
    <div className="rounded-xl border border-line bg-surface-2/50 px-3 py-2">
      <p className="text-[10.5px] font-medium uppercase tracking-wider text-muted">{label}</p>
      <p className={cx('tabular mt-0.5 text-[16px] font-semibold tracking-tight', toneCls)}>
        {value}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1 · Bienvenida — firma de Aziru y nombre de la tienda
// ---------------------------------------------------------------------------

function WelcomeChapter({ active }: ChapterProps): ReactNode {
  const branding = useBranding();
  const reduced = useReducedMotion();
  const variants = reduced ? undefined : RISE;
  const transition = { duration: DURATION.normal, ease: EASE.out };

  return (
    <motion.div
      className="w-full max-w-2xl mx-auto flex flex-col items-center text-center"
      variants={reduced ? undefined : STAGGER_PARENT}
      initial="hidden"
      animate="visible"
    >
      <motion.p
        variants={variants}
        transition={transition}
        className="text-[11px] sm:text-[12px] font-semibold uppercase tracking-[0.24em] text-brand-gold"
      >
        Este sistema inteligente fue creado por
      </motion.p>

      <motion.div variants={variants} transition={transition} className="mt-5">
        <AziruLockup
          play={active}
          markClassName="h-10 sm:h-12 w-auto"
          wordClassName="text-[2.75rem] sm:text-[3.5rem]"
        />
      </motion.div>

      <motion.div aria-hidden variants={variants} transition={transition} className="mt-7 h-px w-16 bg-brand-gold/50" />

      <motion.div variants={variants} transition={transition} className="mt-7 flex flex-col items-center">
        <span className="inline-flex rounded-[23.4%] shadow-card">
          <BrandLogo className="size-14" animated={!reduced} />
        </span>
        <h1 className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight text-balance">
          {branding.name}
        </h1>
      </motion.div>

      <motion.p
        variants={variants}
        transition={transition}
        className="mt-4 max-w-md text-[15px] sm:text-base leading-relaxed text-muted text-pretty"
      >
        Vende, fia, cobra y surte. Y te dice, minuto a minuto, cuanto estas ganando de verdad.
      </motion.p>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// 2 · La caja — vender rapido y ticket por WhatsApp
// ---------------------------------------------------------------------------

/**
 * Tiempos de la caja: dos escaneos, el boton rapido, el cobro, el WhatsApp y
 * la moraleja. La escena cambia de acto al cobrar (beat 4): el ticket se va y
 * entra el comprobante, igual que en la caja real.
 */
const POS_BEATS = [700, 1800, 3100, 4700, 6100, 7700] as const;

function PosChapter({ active, playing }: ChapterProps): ReactNode {
  const reduced = useReducedMotion();
  const count = useBeats(POS_BEATS, active && playing, Boolean(reduced));

  const visibleLines = TICKET_LINES.slice(0, Math.min(count, TICKET_LINES.length));
  const subtotal = ticketTotalCents(visibleLines.length);
  const lastScanned = visibleLines.filter((l) => l.code).at(-1);

  return (
    <Split
      copy={
        <Copy
          kicker="La caja"
          title="Cobra en segundos y manda el ticket por WhatsApp."
          points={[
            'Ticket virtual con el sello de tu tienda: sin rollo, sin impresora, sin gasto.',
            'Botones rapidos para lo que no trae codigo: jitomate, hielo, huevo suelto.',
          ]}
        />
      }
      visual={
        /*
         * La escena tiene dos momentos y no los apila: mientras se escanea es
         * el ticket, y al cobrar se convierte en el comprobante. Es lo que hace
         * la caja de verdad —la confirmacion tapa la lista—, y de paso deja la
         * pantallita del mismo alto en los dos momentos, que es lo que permite
         * que el capitulo entero quepa de una sola vista.
         */
        <Panel title="Caja · sucursal Centro" className="max-w-md mx-auto sm:max-w-none">
          <div className="p-3.5 sm:p-4 space-y-2.5 sm:space-y-3">
            {count < 4 ? (
              <>
                {/* Buscador con el "eco" del lector. */}
                <div
                  className={cx(
                    'flex items-center gap-2.5 h-9 sm:h-10 px-3.5 rounded-xl border bg-surface-2 transition-colors',
                    count >= 1 ? 'border-primary' : 'border-line',
                  )}
                >
                  <span className="size-4 rounded-sm border-2 border-subtle/60 shrink-0" />
                  <span className="tabular font-mono text-[13px] text-muted truncate">
                    {lastScanned ? lastScanned.code : 'Escanea o busca por nombre...'}
                  </span>
                  {lastScanned ? (
                    <span
                      key={visibleLines.length}
                      className="ml-auto text-[11px] font-semibold text-primary animate-in-fade"
                    >
                      leido
                    </span>
                  ) : null}
                </div>

                {/* Botones rapidos; el primero se "presiona" cuando entra el hielo. */}
                <div className="grid grid-cols-4 gap-1.5">
                  {QUICK_BUTTONS.map((label, i) => (
                    <div
                      key={label}
                      className={cx(
                        'rounded-lg border px-1.5 py-1 text-center text-[10px] font-medium leading-tight transition-colors',
                        i === 0 && count >= 3
                          ? 'border-primary bg-primary-soft text-primary'
                          : 'border-line bg-surface text-muted',
                      )}
                    >
                      {label}
                    </div>
                  ))}
                </div>

                {/* Renglones del ticket. */}
                <div className="rounded-xl border border-line divide-y divide-line overflow-hidden">
                  {visibleLines.length === 0 ? (
                    <p className="px-4 py-5 text-center text-[12px] text-subtle">Ticket vacio</p>
                  ) : (
                    visibleLines.map((line) => (
                      <Reveal key={line.name} when className="px-3.5 py-1 sm:py-1.5 bg-surface">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-[13px] font-medium truncate">{line.name}</p>
                            <p className="tabular text-[11px] text-muted">
                              {formatQty(line.qtyMilli, false)} × {formatMoney(line.unitPriceCents)}
                            </p>
                          </div>
                          <span className="tabular text-[14px] font-semibold shrink-0">
                            {formatMoney(demoLineTotal(line))}
                          </span>
                        </div>
                      </Reveal>
                    ))
                  )}
                </div>

                {/* Pie de cobro: el total rueda conforme entran renglones. */}
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[12px] text-muted">{visibleLines.length} articulo(s)</span>
                  <AnimatedAmount
                    cents={subtotal}
                    className="tabular text-2xl font-semibold tracking-tight"
                  />
                </div>

                <Button variant="primary" size="lg" block tabIndex={-1}>
                  Cobrar
                </Button>
              </>
            ) : (
              <>
                {/* El comprobante. El cambio va enorme porque es el numero que
                    el cajero lee de un vistazo, cuenta y entrega. */}
                <div className="rounded-xl border border-line bg-surface-2/50 px-4 py-3.5 sm:py-4 text-center animate-pop">
                  <p className="text-[10.5px] uppercase tracking-wider text-muted">Cambio</p>
                  <p className="tabular text-[38px] sm:text-[42px] leading-none font-semibold tracking-tight text-primary mt-1">
                    {formatMoney(CHANGE_CENTS)}
                  </p>
                  <p className="tabular text-[12px] text-muted mt-2">
                    Total {formatMoney(subtotal)} · Recibido {formatMoney(RECEIVED_CENTS)}
                  </p>
                </div>

                {/* El momento WhatsApp: el ticket viaja como imagen. */}
                <Reveal when={count >= 5} pop>
                  <div className="flex items-center gap-3 rounded-xl border border-primary/30 bg-primary-soft px-4 py-2.5 sm:py-3">
                    <WhatsAppIcon className="size-5 text-primary shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[12.5px] font-semibold text-primary">
                        Ticket #1042 enviado por WhatsApp
                      </p>
                      <p className="text-[11px] text-muted">
                        Con el sello de la tienda y su codigo escaneable.
                      </p>
                    </div>
                  </div>
                </Reveal>

                <Reveal when={count >= 6}>
                  <p className="text-[12px] text-muted text-center">
                    Tres articulos, dos toques y el cliente ya se fue con su ticket.
                  </p>
                </Reveal>
              </>
            )}
          </div>
        </Panel>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// 3 · Granel y bascula
// ---------------------------------------------------------------------------

const SCALE_BEATS = [600, 1700, 2800, 3900, 5200, 6800] as const;

const SCALE_CHIPS = [
  { key: 'prefix', value: SCALE_SEGMENTS.prefix, label: 'bascula', beat: 2 },
  { key: 'item', value: SCALE_SEGMENTS.item, label: GUAJILLO.name.toLowerCase(), beat: 3 },
  { key: 'value', value: SCALE_SEGMENTS.value, label: 'peso en gramos', beat: 4 },
  { key: 'check', value: SCALE_SEGMENTS.check, label: 'verificador', beat: 0 },
] as const;

function ScaleChapter({ active, playing }: ChapterProps): ReactNode {
  const reduced = useReducedMotion();
  const count = useBeats(SCALE_BEATS, active && playing, Boolean(reduced));

  return (
    <Split
      copy={
        <Copy
          kicker="Granel"
          title="Pesa, escanea y ya. Cero tecleo."
          lead="El peso viaja dentro del codigo que imprime tu bascula, y la caja lo entiende solo."
          points={[
            'Etiquetas con codigo propio para lo que no trae de fabrica.',
            'El importe se calcula en centavos enteros: nunca un descuadre por redondeo.',
          ]}
        />
      }
      visual={
        <Panel title="Etiqueta de la bascula" className="max-w-md mx-auto sm:max-w-none">
          <div className="p-3.5 sm:p-4 space-y-3.5">
            <Reveal when={count >= 1} className="flex justify-center">
              <div className="rounded-xl bg-white p-2 sm:p-3 shadow-card">
                <Barcode value={SCALE_CODE} magnification={0.9} />
              </div>
            </Reveal>

            {/* El codigo partido en sus tramos, señalados uno por uno. */}
            <Reveal when={count >= 1}>
              <div className="flex items-stretch justify-center gap-1.5">
                {SCALE_CHIPS.map((chip) => {
                  const on = chip.beat > 0 && count >= chip.beat;
                  return (
                    <div
                      key={chip.key}
                      className={cx(
                        'flex-1 min-w-0 rounded-lg border px-1 py-1.5 text-center transition-colors',
                        on
                          ? 'border-primary bg-primary-soft text-primary'
                          : 'border-line bg-surface-2/50 text-muted',
                      )}
                    >
                      <p className="tabular font-mono text-[13px] font-semibold tracking-wider">
                        {chip.value}
                      </p>
                      <p className="mt-0.5 text-[9px] leading-tight uppercase tracking-wide opacity-80 truncate">
                        {chip.label}
                      </p>
                    </div>
                  );
                })}
              </div>
            </Reveal>

            <Reveal when={count >= 5} pop>
              <div className="rounded-xl border border-line bg-surface px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[13px] font-medium">{GUAJILLO.name}</p>
                    <p className="tabular text-[11px] text-muted">
                      {formatQty(SCALE_QTY_MILLI, true)} kg × {formatMoney(GUAJILLO.pricePerKgCents)} el kilo
                    </p>
                  </div>
                  <span className="tabular text-xl font-semibold text-primary shrink-0">
                    {formatMoney(SCALE_LINE_CENTS)}
                  </span>
                </div>
              </div>
            </Reveal>

            <Reveal when={count >= 6} className="hidden sm:block">
              <p className="text-[12px] text-muted text-center">Un disparo del lector. Nada que teclear.</p>
            </Reveal>
          </div>
        </Panel>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// 4 · Panel en vivo
// ---------------------------------------------------------------------------

const DAY_BEATS = [700, 2200, 4000, 5000, 6000] as const;

function DayChapter({ active, playing }: ChapterProps): ReactNode {
  const reduced = useReducedMotion();
  const count = useBeats(DAY_BEATS, active && playing, Boolean(reduced));

  const points = DAY_HOURS.map((h, i) => ({
    label: h.hour,
    value: h.cents,
    detail: `${h.hour} — ${formatMoney(h.cents)}`,
    highlight: i === PEAK_HOUR_INDEX,
  }));

  return (
    <Split
      copy={
        <Copy
          kicker="Panel en vivo"
          title="Cuanto llevas hoy, sin esperar al corte."
          lead="Venta, tickets y ganancia se mueven con cada cobro, en todas tus pantallas a la vez."
          points={[
            `Tu hora pico —hoy, las ${PEAK_HOUR_LABEL}— para surtir y atender cuando de verdad importa.`,
            'Lo que mas se vende, ordenado por dinero: sabes en que conviene invertir.',
          ]}
        />
      }
      visual={
        <Panel title="Panel · hoy" badge={<LiveDot />} className="max-w-md mx-auto sm:max-w-none">
          <div className="p-3.5 sm:p-4 space-y-3.5">
            <Reveal when={count >= 1}>
              <div className="grid grid-cols-3 gap-2">
                <MiniStat
                  label="Venta"
                  value={<AnimatedAmount cents={DAY_TOTAL_CENTS} short />}
                  tone="primary"
                />
                <MiniStat label="Tickets" value={String(DAY_TICKETS)} />
                <MiniStat label="Ganancia" value={<AnimatedAmount cents={DAY_PROFIT_CENTS} short />} />
              </div>
            </Reveal>

            {/* 64 px de barras: el capitulo entero tiene que caber sin
                desplazarse tambien en una iPad acostada (~700 px de alto). */}
            <Reveal when={count >= 2}>
              <BarChart points={points} height={64} />
            </Reveal>

            {count >= 3 ? (
              /* En el telefono este bloque no aparece: las cifras y la grafica
                 ya cuentan el dia, y el podio era lo que desbordaba el alto. */
              <div className="hidden sm:block rounded-xl border border-line overflow-hidden">
                <div className="px-3.5 py-1.5 bg-surface-2/60 text-[10px] font-semibold uppercase tracking-wider text-muted">
                  Lo que mas se vende
                </div>
                <div className="divide-y divide-line">
                  {TOP_PRODUCTS.map((row, i) => (
                    <Reveal
                      key={row.name}
                      when={count >= i + 3}
                      // En el telefono solo el primero: el argumento es "sabes
                      // que es lo que mas mueve tu dinero", y para eso basta el
                      // numero uno. El podio completo pide un alto que un
                      // telefono no tiene sin obligar a desplazarse.
                      className={cx('px-3.5 py-1', i > 0 && 'hidden sm:block')}
                    >
                      <div className="flex items-center gap-3">
                        <span className="tabular text-[12px] text-subtle w-4">{i + 1}</span>
                        <div className="min-w-0 flex-1">
                          <p className="text-[13px] font-medium truncate">{row.name}</p>
                          <p className="tabular text-[11px] text-muted">{row.qtyLabel}</p>
                        </div>
                        <span className="tabular text-[13px] font-semibold shrink-0">
                          {formatMoney(row.cents)}
                        </span>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </Panel>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// 5 · Inventario
// ---------------------------------------------------------------------------

const INVENTORY_BEATS = [700, 1700, 2700, 3700, 5100] as const;

const STOCK_BADGE: Record<
  DemoStockState,
  { label: string; tone: 'danger' | 'warn' | 'neutral' | 'primary' }
> = {
  out: { label: 'Agotado', tone: 'danger' },
  low: { label: 'Por agotarse', tone: 'warn' },
  negative: { label: 'Por contar', tone: 'warn' },
  ok: { label: 'Sano', tone: 'primary' },
};

function InventoryChapter({ active, playing }: ChapterProps): ReactNode {
  const reduced = useReducedMotion();
  const count = useBeats(INVENTORY_BEATS, active && playing, Boolean(reduced));

  return (
    <Split
      copy={
        <Copy
          kicker="Inventario"
          title="Nunca te quedes sin lo que mas vendes."
          lead="Se descuenta con cada venta y te dice que falta hoy, sin contar nada a mano."
          points={[
            'Te avisa al llegar al punto de reorden, no cuando ya no hay.',
            'Y si el sistema cree que no hay, la venta sigue: el cliente nunca se va con las manos vacias.',
          ]}
        />
      }
      visual={
        <Panel title="Inventario · lo que pide atencion" className="max-w-md mx-auto sm:max-w-none">
          <div className="p-4">
            <div className="rounded-xl border border-line divide-y divide-line overflow-hidden">
              {INVENTORY_ROWS.map((row, i) => {
                const badge = STOCK_BADGE[row.state];
                return (
                  /* El renglon sano solo en pantallas grandes: en el telefono
                     las tres alertas ya cuentan la historia y el cuarto renglon
                     desbordaba el alto del navegador. */
                  <Reveal
                    key={row.name}
                    when={count >= i + 1}
                    className={cx('px-3.5 py-2.5 bg-surface', row.state === 'ok' && 'hidden sm:block')}
                  >
                    <div className="flex items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-medium truncate">{row.name}</p>
                        <p className="text-[11.5px] text-muted text-pretty">{row.note}</p>
                      </div>
                      <span className="tabular text-[13px] font-semibold shrink-0">{row.stockLabel}</span>
                      <Badge tone={badge.tone} className="shrink-0">
                        {badge.label}
                      </Badge>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </Panel>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// 6 · Surtido
// ---------------------------------------------------------------------------

const RESTOCK_BEATS = [700, 1600, 2500, 3800, 5400, 7000] as const;

function RestockChapter({ active, playing }: ChapterProps): ReactNode {
  const reduced = useReducedMotion();
  const count = useBeats(RESTOCK_BEATS, active && playing, Boolean(reduced));

  return (
    <Split
      copy={
        <Copy
          kicker="Surtido inteligente"
          title="Te dice que comprar, cuanto — y te enseña la cuenta."
          lead="Descarta los dias en que un producto estuvo agotado: esa venta no fue cero, fue perdida."
          points={[
            'Llegas al mercado de abastos con el pedido hecho, no con la memoria.',
            'Sin caja negra: cada sugerencia enseña de donde salio el numero.',
          ]}
        />
      }
      visual={
        <div className="max-w-md mx-auto sm:max-w-none space-y-2.5">
          <Panel
            title="Que surtir"
            badge={count >= 3 ? <Badge tone="warn">{URGENCY_LABELS.high}</Badge> : undefined}
          >
            <div className="p-3.5 sm:p-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[14px] font-medium">{RESTOCK.name}</p>
                  <p className="text-[11px] text-muted">{RESTOCK.brand}</p>
                </div>
                {count >= 4 ? <Badge tone="primary">{CONFIDENCE_LABELS.high}</Badge> : null}
              </div>

              <div className="grid grid-cols-3 gap-2">
                <Reveal when={count >= 1}>
                  <MiniStat label="Velocidad" value={RESTOCK.velocityLabel} />
                </Reveal>
                <Reveal when={count >= 2}>
                  <MiniStat label="Quedan" value={`${RESTOCK.stockPieces} pz`} tone="warn" />
                </Reveal>
                <Reveal when={count >= 3}>
                  <MiniStat label="Cobertura" value={RESTOCK.coverLabel} tone="warn" />
                </Reveal>
              </div>

              <Reveal when={count >= 4} pop>
                <div className="rounded-xl border border-primary/30 bg-primary-soft px-4 py-2.5 flex items-center justify-between gap-3">
                  <span className="text-[13px] font-medium text-primary">Sugerencia</span>
                  <span className="tabular text-2xl font-semibold tracking-tight text-primary">
                    Pide {RESTOCK.suggestedPieces}
                  </span>
                </div>
              </Reveal>

              <Reveal when={count >= 5}>
                <p className="text-[12px] leading-snug text-muted text-pretty">{RESTOCK.reason}</p>
              </Reveal>
            </div>
          </Panel>

          {/*
            La prueba de que el motor se calla cuando no sabe. Es un argumento
            de segunda linea —el de primera es la sugerencia de arriba— y en un
            telefono es lo que sobra para que el capitulo quepa de una vista.
          */}
          <Reveal when={count >= 6} className="hidden sm:block">
            <Panel>
              <div className="px-4 py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[13px] font-medium truncate">{RESTOCK_LOW.name}</p>
                  <p className="text-[11px] text-muted">
                    Solo {RESTOCK_LOW.daysOfHistory} dias de historial: prefiere callarse a inventar.
                  </p>
                </div>
                <Badge tone="neutral" className="shrink-0">
                  {CONFIDENCE_LABELS.low}
                </Badge>
              </div>
            </Panel>
          </Reveal>
        </div>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// 7 · Merma y margen real
// ---------------------------------------------------------------------------

const SHRINK_BEATS = [700, 1600, 2700, 4100, 5700] as const;

function ShrinkChapter({ active, playing }: ChapterProps): ReactNode {
  const reduced = useReducedMotion();
  const count = useBeats(SHRINK_BEATS, active && playing, Boolean(reduced));

  const nominal = Math.round(SHRINK.nominalPct);
  const real = Math.round(SHRINK.realPct);

  return (
    <Split
      copy={
        <Copy
          kicker="Margen real"
          title="Lo que de verdad ganas, sin adornos."
          lead="El costal se deshidrata solo: si de cada kilo vendes 0.940, tu costo real es mas alto. El sistema lo calcula."
          points={[
            'Separa la merma que la naturaleza explica de la que hay que revisar.',
            'Es el numero que casi ningun dueño ha visto de su negocio.',
          ]}
        />
      }
      visual={
        <Panel title={`Kardex · ${GUAJILLO.name}`} className="max-w-md mx-auto sm:max-w-none">
          <div className="p-3.5 sm:p-4 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Reveal when={count >= 1}>
                <MiniStat label="Compraste" value={`${formatQty(SHRINK.boughtMilli, true)} kg`} />
              </Reveal>
              <Reveal when={count >= 2}>
                <MiniStat label="Vendiste" value={`${formatQty(SHRINK.soldMilli, true)} kg`} />
              </Reveal>
            </div>

            {/* El desglose del kardex solo en pantallas grandes: en el telefono
                las barras de margen son el argumento y esto lo desbordaba. */}
            <Reveal when={count >= 3} className="hidden sm:block">
              <div className="rounded-xl border border-line divide-y divide-line overflow-hidden">
                <div className="px-3.5 py-2 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-[13px] font-medium">Merma natural</p>
                    <p className="text-[11px] text-muted">Lo que la deshidratacion explica</p>
                  </div>
                  <span className="tabular text-[13px] font-semibold text-muted">
                    -{formatQty(SHRINK.naturalMilli, true)} kg
                  </span>
                </div>
                <div className="px-3.5 py-2 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-[13px] font-medium">Ajuste</p>
                    <p className="text-[11px] text-muted">Lo que no explica; eso si se revisa</p>
                  </div>
                  <span className="tabular text-[13px] font-semibold text-warn">
                    -{formatQty(SHRINK.adjustMilli, true)} kg
                  </span>
                </div>
              </div>
            </Reveal>

            <Reveal when={count >= 4}>
              <div className="space-y-2.5 pt-0.5">
                <MarginBar label="Margen en papel" pct={nominal} max={nominal} cls="bg-surface-3" />
                <MarginBar
                  label={`Margen real · costo ${formatMoney(SHRINK.realCostPerKgCents)}/kg`}
                  pct={real}
                  max={nominal}
                  cls="bg-primary"
                />
              </div>
            </Reveal>

            <Reveal when={count >= 5}>
              <p className="text-[12px] text-muted text-pretty">
                {nominal - real} puntos de diferencia entre creer que ganas y saberlo.
              </p>
            </Reveal>
          </div>
        </Panel>
      }
    />
  );
}

function MarginBar({
  label,
  pct,
  max,
  cls,
}: {
  label: string;
  pct: number;
  max: number;
  cls: string;
}): ReactNode {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-[11.5px] text-muted">{label}</span>
        <span className="tabular text-[13px] font-semibold">{pct}%</span>
      </div>
      <div className="h-2 rounded-full bg-surface-2 overflow-hidden">
        <div
          className={cx('h-full rounded-full transition-[width] duration-700', cls)}
          style={{ width: `${(pct / max) * 100}%` }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 8 · Credito
// ---------------------------------------------------------------------------

const CREDIT_BEATS = [800, 2200, 3600, 5200] as const;

const COLLECTION_TONE: Record<
  DemoCollectionUrgency,
  { dot: string; tone: 'danger' | 'warn' | 'neutral' }
> = {
  vencido: { dot: 'bg-danger', tone: 'danger' },
  vence_hoy: { dot: 'bg-warn', tone: 'warn' },
  al_corriente: { dot: 'bg-subtle', tone: 'neutral' },
};

function CreditChapter({ active, playing }: ChapterProps): ReactNode {
  const reduced = useReducedMotion();
  const count = useBeats(CREDIT_BEATS, active && playing, Boolean(reduced));

  const balance = count >= 1 ? BALANCE_AFTER_SALE : CREDIT_CUSTOMER.openingBalanceCents;
  const limitPct = Math.round((balance / CREDIT_CUSTOMER.limitCents) * 100);

  return (
    <Split
      copy={
        <Copy
          kicker="Credito"
          title="Fia sin miedo: el sistema te lo cobra."
          lead="Cada mañana te dice a quien cobrarle, en que orden y por que."
          points={[
            'Limite por cliente que se respeta solo, aunque haya dos cajas abiertas.',
            'Aprende como paga cada quien y lo mide contra su propio plazo.',
          ]}
        />
      }
      visual={
        <Panel title="Credito · agenda de hoy" className="max-w-md mx-auto sm:max-w-none">
          <div className="p-3.5 sm:p-4 space-y-2.5">
            {/* Se fia desde la caja y el limite reacciona al instante. */}
            <div className="rounded-xl border border-line bg-surface px-3.5 py-2.5">
              <div className="flex items-baseline justify-between gap-3 mb-1.5">
                <span className="text-[12px] text-muted truncate">
                  {count >= 1
                    ? `Se le fio ${formatMoney(CREDIT_SALE_CENTS)} a ${CREDIT_CUSTOMER.name}`
                    : CREDIT_CUSTOMER.name}
                </span>
                <span className="tabular text-[12px] text-muted shrink-0">
                  <AnimatedAmount cents={balance} className="font-semibold text-ink" /> de{' '}
                  {formatMoney(CREDIT_CUSTOMER.limitCents)}
                </span>
              </div>
              <div className="h-2 rounded-full bg-surface-2 overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary transition-[width] duration-500"
                  style={{ width: `${Math.min(limitPct, 100)}%` }}
                />
              </div>
            </div>

            {/* Los recordatorios del dia, con su explicacion. En el telefono
                solo el primero: para vender la idea basta el renglon vencido
                con su razon, y el segundo era lo que sacaba el capitulo del
                alto que deja el navegador del celular. */}
            {AGENDA.map((entry, i) => {
              const tone = COLLECTION_TONE[entry.urgency];
              return (
                <Reveal
                  key={entry.name}
                  when={count >= i + 2}
                  className={cx(i > 0 && 'hidden sm:block')}
                >
                  <div className="rounded-xl border border-line bg-surface px-3.5 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <span className={cx('size-2 rounded-full shrink-0', tone.dot)} />
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-medium truncate">{entry.name}</p>
                        <p className="text-[11px] text-muted truncate">{entry.habitLabel}</p>
                      </div>
                      <span className="tabular text-[14px] font-semibold shrink-0">
                        {formatMoney(entry.owedCents)}
                      </span>
                      <Badge tone={tone.tone} className="shrink-0">
                        {COLLECTION_URGENCY_LABELS[entry.urgency]}
                      </Badge>
                    </div>
                    <p className="mt-1.5 text-[12px] leading-snug text-muted text-pretty">
                      {entry.reason}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </Panel>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// 9 · Gastos y corte
// ---------------------------------------------------------------------------

const MONEY_BEATS = [600, 1400, 2200, 3600, 5000] as const;

function MoneyChapter({ active, playing }: ChapterProps): ReactNode {
  const reduced = useReducedMotion();
  const count = useBeats(MONEY_BEATS, active && playing, Boolean(reduced));

  return (
    <Split
      copy={
        <Copy
          kicker="Gastos y corte"
          title="Cada peso que entra y sale, contado."
          lead="El gasto se captura en segundos y el corte compara solo lo que deberia haber contra lo que hay."
          points={[
            'Sabes en que se va el dinero: mercancia, servicios, fletes.',
            'Tu ganancia deja de ser una corazonada de fin de mes.',
          ]}
        />
      }
      visual={
        <Panel title="El dinero del dia" className="max-w-md mx-auto sm:max-w-none">
          <div className="p-3.5 sm:p-4 space-y-2.5">
            <div className="rounded-xl border border-line divide-y divide-line overflow-hidden">
              {EXPENSES_ROWS.map((row, i) => (
                /* En el telefono solo el primer gasto: el corte de abajo es el
                   argumento, y la lista completa desbordaba el alto. */
                <Reveal
                  key={row.label}
                  when={count >= i + 1}
                  className={cx('px-3.5 py-1.5 bg-surface', i > 0 && 'hidden sm:block')}
                >
                  <div className="flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-[12.5px] font-medium truncate">{row.label}</p>
                      <p className="text-[10.5px] text-muted">{row.category}</p>
                    </div>
                    <span className="tabular text-[12.5px] font-semibold shrink-0">
                      -{formatMoney(row.cents)}
                    </span>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal when={count >= 4}>
              <div className="rounded-xl border border-line overflow-hidden">
                <div className="px-3.5 py-1.5 bg-surface-2/60 text-[10px] font-semibold uppercase tracking-wider text-muted">
                  Corte de caja · turno de la tarde
                </div>
                <div className="px-3.5 py-1.5 space-y-0.5 text-[12px]">
                  <CutRow label="Fondo inicial" cents={CASH_CUT.fundCents} />
                  <CutRow label="Ventas en efectivo" cents={CASH_CUT.cashSalesCents} />
                  <CutRow label="Gastos pagados" cents={-CASH_CUT.expensesCents} />
                  <div className="border-t border-line pt-1 mt-1">
                    <CutRow label="Deberia haber" cents={CASH_CUT.expectedCents} strong />
                    <CutRow label="Se conto" cents={CASH_CUT.countedCents} strong />
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal when={count >= 5} pop>
              <div className="flex items-center justify-between gap-3 rounded-xl border border-warn/30 bg-warn-soft px-4 py-2">
                <span className="text-[13px] font-medium text-warn">Diferencia</span>
                <span className="tabular text-[15px] font-semibold text-warn">
                  {formatMoney(CASH_CUT.differenceCents)}
                </span>
              </div>
            </Reveal>
          </div>
        </Panel>
      }
    />
  );
}

function CutRow({
  label,
  cents,
  strong = false,
}: {
  label: string;
  cents: number;
  strong?: boolean;
}): ReactNode {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className={strong ? 'font-medium text-ink' : 'text-muted'}>{label}</span>
      <span className={cx('tabular', strong ? 'font-semibold' : 'text-muted')}>
        {formatMoney(cents)}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 10 · Multisucursal
// ---------------------------------------------------------------------------

const STORES_BEATS = [700, 1600, 2500, 3800, 5200] as const;

function StoresChapter({ active, playing }: ChapterProps): ReactNode {
  const reduced = useReducedMotion();
  const count = useBeats(STORES_BEATS, active && playing, Boolean(reduced));

  return (
    <Split
      copy={
        <Copy
          kicker="Multisucursal"
          title="Todas tus tiendas, en vivo, desde tu celular."
          lead="Cambias de sucursal en un toque y ves ventas, almacen y panel de cada una — estes donde estes."
          points={[
            'Los numeros de una tienda jamas se revuelven con los de otra.',
            'Abrir la segunda no te obliga a cambiar de sistema.',
          ]}
        />
      }
      visual={
        <Panel title="Tus sucursales" badge={<LiveDot />} className="max-w-md mx-auto sm:max-w-none">
          <div className="p-3.5 sm:p-4 space-y-2.5">
            <div className="rounded-xl border border-line divide-y divide-line overflow-hidden">
              {STORES_LIVE.map((store, i) => (
                <Reveal key={store.name} when={count >= i + 1} className="px-3.5 py-2.5 bg-surface">
                  <div className="flex items-center gap-3">
                    <Store className="size-4 text-muted shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-medium truncate">Sucursal {store.name}</p>
                      <p className="tabular text-[11px] text-muted">{store.tickets} tickets hoy</p>
                    </div>
                    <span className="tabular text-[15px] font-semibold shrink-0">
                      {formatMoney(store.cents)}
                    </span>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal when={count >= 4} pop>
              <div className="flex items-center justify-between gap-3 rounded-xl border border-primary/30 bg-primary-soft px-4 py-2.5">
                <span className="text-[13px] font-medium text-primary">
                  Hoy · {STORES_TOTAL_TICKETS} tickets
                </span>
                <AnimatedAmount
                  cents={STORES_TOTAL_CENTS}
                  className="tabular text-xl font-semibold tracking-tight text-primary"
                />
              </div>
            </Reveal>

            {/* En el telefono sobra decirlo: lo esta viendo en su bolsillo. */}
            <Reveal when={count >= 5} className="hidden sm:block">
              <div className="flex items-center gap-3 rounded-xl border border-line bg-surface-2/50 px-4 py-2.5">
                <Smartphone className="size-5 text-muted shrink-0" />
                <p className="text-[12px] text-muted text-pretty">
                  La misma aplicacion en la caja, en la oficina y en tu bolsillo.
                </p>
              </div>
            </Reveal>
          </div>
        </Panel>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// 11 · A tu medida
// ---------------------------------------------------------------------------

/*
 * NO SE PROMETE "SIN MENSUALIDAD" NI "SIN NUBE".
 *
 * Este capitulo cerraba antes con esas dos frases y las dos eran un problema.
 * "Sin mensualidad" contradice el modelo de negocio del estudio —hay cobro por
 * mantenimiento, mejoras, dominio y base—, y prometerselo al cliente en la
 * demostracion es cavar el hoyo donde caera la conversacion del precio. "Sin
 * esperar a ninguna nube" dejo de ser cierto en cuanto el sistema empezo a
 * vivir tambien en linea, y una promesa que el cliente puede desmentir el
 * primer dia cuesta mas que la que nunca se hizo.
 *
 * Lo que queda son cuatro cosas que el sistema SI hace y que se sostienen sin
 * importar donde corra. La ultima —que crece contigo— es ademas la que prepara
 * la conversacion de la mensualidad en lugar de sabotearla.
 */
const OWN_BEATS = [500, 1200, 1900, 2600] as const;

const OWN_CARDS = [
  {
    icon: Zap,
    title: 'Cobrar es inmediato',
    body: 'Escanear, cobrar y seguir. La fila del mostrador no se detiene a esperar nada.',
  },
  {
    icon: Archive,
    title: 'Tu historial, respaldado',
    body: 'La base se respalda sola cada pocas horas. La historia de tu negocio no se pierde.',
  },
  {
    icon: ShieldCheck,
    title: 'Todo queda firmado',
    body: 'Cada quien ve lo que le toca, y cada cambio de precio o cancelacion queda con nombre.',
  },
  {
    icon: Sparkles,
    title: 'Crece contigo',
    body: 'Lo que hoy resuelve tu mostrador, mañana resuelve lo que tu negocio necesite.',
  },
] as const;

function OwnChapter({ active, playing }: ChapterProps): ReactNode {
  const reduced = useReducedMotion();
  const count = useBeats(OWN_BEATS, active && playing, Boolean(reduced));

  return (
    <Split
      copy={
        <Copy
          kicker="A tu medida"
          title="Hecho para tu tienda. Y siempre contigo."
          lead="No es un molde al que tu negocio se tenga que acomodar: esta hecho para como trabaja tu tienda."
        />
      }
      visual={
        /* Dos columnas tambien en el telefono: apiladas, las cuatro tarjetas
           rebasaban por mucho el alto que deja el navegador del celular. */
        <div className="max-w-md mx-auto sm:max-w-none grid grid-cols-2 gap-2 sm:gap-2.5">
          {OWN_CARDS.map((card, i) => (
            <Reveal key={card.title} when={count >= i + 1}>
              <div className="h-full rounded-2xl border border-line bg-surface shadow-card p-3 sm:p-4">
                <card.icon className="size-4.5 sm:size-5 text-primary" aria-hidden />
                <p className="mt-2 sm:mt-2.5 text-[13px] sm:text-[14px] font-semibold">{card.title}</p>
                <p className="mt-1 text-[11.5px] sm:text-[12.5px] leading-snug text-muted text-pretty">
                  {card.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// 12 · Cierre — firmado por Aziru
// ---------------------------------------------------------------------------

function ClosingChapter({ active, onRestart, onExit }: ChapterProps): ReactNode {
  const branding = useBranding();
  const reduced = useReducedMotion();
  const variants = reduced ? undefined : RISE;
  const transition = { duration: DURATION.normal, ease: EASE.out };

  return (
    <motion.div
      className="w-full max-w-xl mx-auto flex flex-col items-center text-center"
      variants={reduced ? undefined : STAGGER_PARENT}
      initial="hidden"
      animate="visible"
    >
      <motion.h2
        variants={variants}
        transition={transition}
        className="text-[28px] sm:text-5xl font-semibold tracking-tight text-balance"
      >
        Tu tienda, bajo control.
      </motion.h2>

      <motion.p
        variants={variants}
        transition={transition}
        className="mt-2.5 sm:mt-3.5 max-w-md text-[14px] sm:text-base leading-relaxed text-muted text-pretty"
      >
        Asi trabaja {branding.name}: rapido en el mostrador, inteligente por dentro.
      </motion.p>

      {/* En renglon tambien en el telefono: apilados, los dos botones mas la
          placa de Aziru rebasan el alto que deja el navegador del celular. */}
      <motion.div
        variants={variants}
        transition={transition}
        className="mt-5 sm:mt-6 flex flex-row flex-wrap items-center justify-center gap-2.5 sm:gap-3"
      >
        <Button variant="primary" size="lg" onClick={onExit} className="px-6 sm:px-8">
          Entrar al sistema
        </Button>
        <Button variant="secondary" size="lg" onClick={onRestart}>
          Volver a verlo
        </Button>
      </motion.div>

      <motion.div
        variants={variants}
        transition={transition}
        className="mt-5 sm:mt-8 w-full max-w-sm"
      >
        <AziruCard play={active} />
      </motion.div>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// El indice del recorrido
// ---------------------------------------------------------------------------

/*
 * EL ORDEN ES EL ARGUMENTO, Y VA DE LO ANCHO A LO HONDO.
 *
 * Primero lo que cualquier dueño reconoce en tres segundos —vender y cuanto
 * llevas hoy—, y enseguida MULTISUCURSAL, que es el capitulo que mas
 * entusiasma cuando el negocio tiene o piensa tener mas de una tienda: ver las
 * tres en vivo desde el celular es la promesa mas grande del sistema y no
 * puede quedar sepultada en el capitulo diez, donde muchos ya no llegan.
 *
 * Va justo despues del panel a proposito: el panel acaba de demostrar que las
 * cifras se mueven solas en una tienda, asi que "y ahora multiplicalo por
 * todas" se entiende sin explicar nada. Es el mismo dato, mas grande.
 *
 * De ahi el recorrido baja al mostrador (granel) y sigue hacia lo que se
 * descubre operando: que falta, cuanto pedir, cuanto se gana de verdad, quien
 * debe, en que se va el dinero. Cada `next` es la pregunta que el capitulo
 * siguiente contesta; si se reordena un capitulo hay que reescribir dos
 * puentes, no uno.
 */
export const CHAPTERS: readonly ChapterDef[] = [
  {
    id: 'bienvenida',
    title: 'Bienvenida',
    duration: 9_000,
    identity: true,
    next: 'Empecemos donde empieza el dinero: el mostrador.',
    Component: WelcomeChapter,
  },
  {
    id: 'caja',
    title: 'La caja',
    duration: 13_000,
    next: 'Ya estas vendiendo. ¿Como va la tienda en este momento?',
    Component: PosChapter,
  },
  {
    id: 'panel',
    title: 'Panel en vivo',
    duration: 12_000,
    next: 'Eso es una tienda. ¿Y si tienes mas de una?',
    Component: DayChapter,
  },
  {
    id: 'sucursales',
    title: 'Multisucursal',
    duration: 12_000,
    next: 'Volvamos al mostrador: ¿y lo que se vende por kilo?',
    Component: StoresChapter,
  },
  {
    id: 'granel',
    title: 'Granel',
    duration: 11_000,
    next: 'Ya sabes como entra el dinero. ¿Y que te queda en el almacen?',
    Component: ScaleChapter,
  },
  {
    id: 'inventario',
    title: 'Inventario',
    duration: 11_000,
    next: 'Ya sabes que falta. ¿Cuanto pides de cada cosa?',
    Component: InventoryChapter,
  },
  {
    id: 'surtido',
    title: 'Surtido inteligente',
    duration: 12_500,
    next: 'Compras bien. ¿Pero de verdad ganas lo que crees?',
    Component: RestockChapter,
  },
  {
    id: 'merma',
    title: 'Margen real',
    duration: 11_500,
    next: 'Ya sabes lo que ganas. ¿Y lo que te deben?',
    Component: ShrinkChapter,
  },
  {
    id: 'credito',
    title: 'Credito',
    duration: 12_000,
    next: 'Cobras a tiempo. ¿Y lo que sale del cajon?',
    Component: CreditChapter,
  },
  {
    id: 'dinero',
    title: 'Gastos y corte',
    duration: 11_500,
    next: 'Una tienda cuadrada. ¿Y este sistema, de quien es?',
    Component: MoneyChapter,
  },
  {
    id: 'tuyo',
    title: 'A tu medida',
    duration: 10_000,
    next: 'Eso es todo. ¿Lo estrenamos?',
    Component: OwnChapter,
  },
  {
    id: 'cierre',
    title: 'Tu tienda, bajo control',
    duration: 12_000,
    identity: true,
    Component: ClosingChapter,
  },
];
